/**
 * Pitch detection using autocorrelation algorithm
 * Based on the algorithm described by Chris Wilson
 * https://github.com/cwilso/PitchDetect
 */

export interface PitchDetectionResult {
  /** Detected frequency in Hz */
  frequency: number;
  /** Correlation value (0-1, higher is better) */
  correlation: number;
}

export class PitchDetector {
  private sampleRate: number;
  private minCorrelation: number;

  constructor(sampleRate: number, minCorrelation = 0.9) {
    this.sampleRate = sampleRate;
    this.minCorrelation = minCorrelation;
  }

  /**
   * Detect pitch from audio buffer using autocorrelation
   * @param buffer Audio buffer (Float32Array)
   * @returns Pitch detection result or null if no pitch detected
   */
  detectPitch(buffer: Float32Array): PitchDetectionResult | null {
    // Calculate RMS to check if there's enough signal
    const rms = this.calculateRMS(buffer);

    // Debug logging
    if (typeof (window as any).__pitchDetectCount === 'undefined') {
      (window as any).__pitchDetectCount = 0;
    }
    (window as any).__pitchDetectCount++;

    if ((window as any).__pitchDetectCount % 60 === 0) {
      console.log(`[PitchDetect] RMS: ${rms.toFixed(4)}, Min threshold: 0.01`);
    }

    if (rms < 0.01) {
      return null; // Signal too weak
    }

    // Find the fundamental frequency using autocorrelation
    const correlations = this.autoCorrelate(buffer);
    if (!correlations) {
      if ((window as any).__pitchDetectCount % 60 === 0) {
        console.log(`[PitchDetect] Autocorrelation failed`);
      }
      return null;
    }

    const { period, correlation } = correlations;

    // Check if correlation is strong enough
    if (correlation < this.minCorrelation) {
      if ((window as any).__pitchDetectCount % 60 === 0) {
        console.log(`[PitchDetect] Correlation ${correlation.toFixed(3)} < ${this.minCorrelation} (too weak)`);
      }
      return null;
    }

    const frequency = this.sampleRate / period;

    // Filter out unreasonable frequencies (below 30 Hz or above 4186 Hz - C8)
    if (frequency < 30 || frequency > 4186) {
      if ((window as any).__pitchDetectCount % 60 === 0) {
        console.log(`[PitchDetect] Frequency ${frequency.toFixed(2)} Hz out of range (30-4186 Hz)`);
      }
      return null;
    }

    console.log(`[PitchDetect] ✓ Detected: ${frequency.toFixed(2)} Hz, correlation: ${correlation.toFixed(3)}`);
    return { frequency, correlation };
  }

  /**
   * Calculate RMS (Root Mean Square) of the signal
   */
  private calculateRMS(buffer: Float32Array): number {
    let sum = 0;
    for (let i = 0; i < buffer.length; i++) {
      sum += buffer[i] * buffer[i];
    }
    return Math.sqrt(sum / buffer.length);
  }

  /**
   * Autocorrelation algorithm to find the fundamental frequency
   * Compares the signal with delayed copies of itself to find periodicity
   */
  private autoCorrelate(
    buffer: Float32Array
  ): { period: number; correlation: number } | null {
    const size = buffer.length;
    const maxSamples = Math.floor(size / 2);

    // Calculate minimum offset based on max frequency (4186 Hz = C8)
    // This prevents detecting offset=0 or harmonics that are too high
    const minOffset = Math.floor(this.sampleRate / 4186);

    let bestOffset = -1;
    let bestCorrelation = 0;
    let foundGoodCorrelation = false;

    // Calculate correlations for different offsets
    const correlations = new Array(maxSamples);

    // Start from minOffset to skip the initial perfect correlation at 0
    for (let offset = minOffset; offset < maxSamples; offset++) {
      let correlation = 0;

      for (let i = 0; i < maxSamples; i++) {
        correlation += Math.abs(buffer[i] - buffer[i + offset]);
      }

      correlation = 1 - correlation / maxSamples;
      correlations[offset] = correlation;

      // Look for the first peak with good correlation
      if (correlation > 0.9 && !foundGoodCorrelation) {
        // Check if this is a peak (higher than neighbors)
        const prevCorr = offset > minOffset ? correlations[offset - 1] : 0;

        if (correlation > prevCorr) {
          foundGoodCorrelation = true;
          bestCorrelation = correlation;
          bestOffset = offset;
        }
      } else if (foundGoodCorrelation && offset === bestOffset + 1) {
        // Check if we're still climbing (to find the peak)
        if (correlation > bestCorrelation) {
          bestCorrelation = correlation;
          bestOffset = offset;
        }
      }
    }

    if (!foundGoodCorrelation || bestOffset < minOffset) {
      return null;
    }

    // Parabolic interpolation for sub-sample accuracy
    let refinedOffset = bestOffset;

    if (bestOffset > minOffset && bestOffset < correlations.length - 1) {
      const a = correlations[bestOffset - 1] || 0;
      const b = correlations[bestOffset];
      const c = correlations[bestOffset + 1] || 0;

      // Only interpolate if we have a proper peak
      if (a !== c && b >= a && b >= c) {
        const delta = (a - c) / (2 * (2 * b - a - c));
        refinedOffset = bestOffset + delta;
      }
    }

    // Ensure period is positive
    if (refinedOffset <= 0) {
      return null;
    }

    return {
      period: refinedOffset,
      correlation: bestCorrelation,
    };
  }

  /**
   * Update minimum correlation threshold
   */
  setMinCorrelation(minCorrelation: number): void {
    this.minCorrelation = minCorrelation;
  }
}
